import { forwardRef } from '@hover/blueprint';
import { assertUnreachable } from '@hover/utilities';
import React from 'react';

import { TranslationNamespace } from 'src/i18n/types';
import { instancesToMaterials } from 'src/modules/homeownerApp/components/Customize/SegmentationOverlay';
import { useImperativeTracking } from 'src/modules/homeownerApp/components/Customize/useImperativeTracking';
import { useTranslate } from 'src/shared/hooks';
import { useIsMobileSize } from 'src/shared/hooks/useIsMobileSize';

import type { MaterialRegion } from '../../constants/customizationCategories';
import { warn } from '../../helpers/logging';
import type { DesignerMaterialProduct } from '../../types';
import { isMaterialProduct } from '../../types';
import { Palette } from '../Palette';
import { MaterialPaletteSheet } from '../Palette/MaterialPaletteSheet';

import type { CustomizeControlsProps } from './CustomizeControls';
import { CustomizeControls } from './CustomizeControls';
import { productTrackingData } from './tracking';
import {
  useCustomization,
  useCustomizationSegmentation,
} from './useCustomization';
import { usePalettes } from './usePalettes';

export interface CustomizeMaterialProps
  extends Partial<CustomizeControlsProps> {
  region: MaterialRegion;
  spaceType: string;
}

const CustomizeMaterial = forwardRef<CustomizeMaterialProps, 'div'>(
  ({ region, spaceType, ...props }, ref) => {
    const {
      category,
      clearCustomization,
      customizeImage,
      customization,
      isCustomizing,
      baseImage,
    } = useCustomization();
    const { selectedSegments, handlePlacementMode } =
      useCustomizationSegmentation();
    const { trackingRef } = useImperativeTracking();

    const {
      materials,
      hasUserSelectedPalette,
      isLoading,
      selectedPalette,
      setSelectedPalette,
      onSelectPaletteOpen,
      onSelectPaletteClose,
      isSelectPaletteOpen,
    } = usePalettes();

    const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
    const isMobile = useIsMobileSize();

    const aspect = spaceType === 'exterior' ? 'exterior' : 'interior';

    const header = (() => {
      switch (region) {
        case 'roof':
          return t('palette.cardHeaderRoof');
        case 'floor':
          return t('palette.cardHeaderFloor');
        case 'walls':
          return t('palette.cardHeaderWalls');
        case 'windows':
          return t('palette.cardHeaderWindows');
        case 'cabinet':
          return t('palette.cardHeaderCabinet');
        case 'garage_door':
          return t('palette.cardHeaderGarageDoor');
        default:
          return assertUnreachable(region);
      }
    })();

    const handleSelectItem = (
      product: DesignerMaterialProduct | null,
      regenerate?: boolean,
    ) => {
      if (product) {
        const materialClassesEdited =
          selectedSegments && handlePlacementMode === 'material'
            ? {
                material_classes_edited:
                  instancesToMaterials(selectedSegments).length,
              }
            : undefined;

        trackingRef.current?.productSelected({
          ...productTrackingData(selectedPalette, product),
          ...materialClassesEdited,
          edit_type: category?.region,
          selection_type: regenerate ? 'Re-generate' : 'First Generation',
          ...(baseImage.fromSample && { track_id: 'edit-sample' }),
        });
        void customizeImage(product);
      } else {
        clearCustomization();
      }
    };

    const handleSelectPaletteOpen = () => {
      trackingRef.current?.moduleViewed({
        edit_type: category?.region,
        track_id: `${
          baseImage.fromSample ? 'sample-' : ''
        }product-group-selection-module`,
      });
      onSelectPaletteOpen();
    };

    const handleSelectPalette = (palette: string) => {
      trackingRef.current?.buttonPressed({
        button_text: palette,
        edit_type: category?.region,
        primary_cta: false,
        product_group: palette,
        track_id: `${
          baseImage.fromSample ? 'sample-' : ''
        }product-group-selected`,
      });
      setSelectedPalette(palette);
      onSelectPaletteClose();
    };

    const currentProduct = customization?.product;

    if (!category) {
      warn('CustomizeMaterial: attempted to render without a category');
      return null;
    }

    return (
      <CustomizeControls
        onSelectPaletteClick={handleSelectPaletteOpen}
        paletteLabel={selectedPalette}
        {...props}
        ref={ref}
      >
        <Palette
          aspect={aspect}
          category={category}
          disableRefresh={baseImage.fromSample}
          include3dFusionMaterials={baseImage.is3dFusionCompatible}
          isRefreshable={!isCustomizing}
          itemProps={{
            borderRadius: '300',
            colorScheme: isMobile ? 'light' : 'dark',
          }}
          onReselectItem={material => {
            if (material) {
              handleSelectItem(material, true);
            }
          }}
          onSelectItem={handleSelectItem}
          selectedItem={
            isMaterialProduct(currentProduct) &&
            customization?.region === category?.region
              ? currentProduct
              : null
          }
          selectedPalette={selectedPalette}
          type="material"
        />
        <MaterialPaletteSheet
          header={header}
          isLoading={isLoading}
          isOpen={isSelectPaletteOpen}
          materials={materials}
          onClose={onSelectPaletteClose}
          onSelect={handleSelectPalette}
          region={region}
          selectedPalette={
            !hasUserSelectedPalette ? undefined : selectedPalette
          }
        />
      </CustomizeControls>
    );
  },
);

export { CustomizeMaterial };
